import { useState, useEffect } from "react";
import * as MP4Box from "mp4box";

/**
 * Custom hook to detect the framerate of a video file.
 * Tries container parsing (MP4Box) first for accuracy, then falls back to frame sampling.
 */
export default function useVideoFramerate(videoFile) {
    const [estimatedFps, setEstimatedFps] = useState(null);
    const [isDetectingFps, setIsDetectingFps] = useState(false);
    const [detectionMethod, setDetectionMethod] = useState(null);

    useEffect(() => {
        if (!videoFile) {
            setEstimatedFps(null);
            setIsDetectingFps(false);
            setDetectionMethod(null);
            return;
        }

        setIsDetectingFps(true);
        setEstimatedFps(null);
        setDetectionMethod(null);

        const detect = async () => {
            try {
                // === Try MP4Box container parsing first (most accurate) ===
                const arrayBuffer = await videoFile.arrayBuffer();
                const mp4boxFile = MP4Box.createFile();

                const fpsFromContainer = await new Promise((resolve) => {
                    const timeout = setTimeout(() => {
                        resolve(null);
                    }, 4000);

                    mp4boxFile.onReady = (info) => {
                        clearTimeout(timeout);
                        const videoTrack = info.tracks.find((t) => t.type === "video");
                        if (videoTrack && videoTrack.timescale && videoTrack.stts?.entries?.length > 0) {
                            let totalDuration = 0;
                            let totalSamples = 0;

                            videoTrack.stts.entries.forEach((entry) => {
                                totalDuration += entry.sample_delta * entry.sample_count;
                                totalSamples += entry.sample_count;
                            });

                            if (totalSamples > 0) {
                                const avgSampleDuration = totalDuration / totalSamples;
                                const fps = videoTrack.timescale / avgSampleDuration;
                                if (fps > 10 && fps < 200) {
                                    resolve(fps);
                                    return;
                                }
                            }
                        }
                        resolve(null);
                    };

                    mp4boxFile.onError = () => {
                        clearTimeout(timeout);
                        resolve(null);
                    };

                    mp4boxFile.appendBuffer(arrayBuffer);
                    mp4boxFile.flush();
                });

                if (fpsFromContainer) {
                    setEstimatedFps(Math.round(fpsFromContainer * 100) / 100);
                    setDetectionMethod("container");
                    return;
                }

                // === Fallback: Frame sampling using requestVideoFrameCallback ===
                setDetectionMethod("sampling");

                const video = document.createElement("video");
                video.muted = true;
                video.playsInline = true;
                const objectUrl = URL.createObjectURL(videoFile);
                video.src = objectUrl;

                const fpsFromSampling = await new Promise((resolve) => {
                    const timestamps = [];
                    let handle = null;

                    const timeout = setTimeout(() => {
                        resolve(null);
                    }, 8000);

                    const callback = (now, metadata) => {
                        if (metadata && typeof metadata.mediaTime === "number") {
                            timestamps.push(metadata.mediaTime);
                        }

                        if (timestamps.length >= 25) {
                            clearTimeout(timeout);
                            if (handle) video.cancelVideoFrameCallback?.(handle);
                            URL.revokeObjectURL(objectUrl);
                            resolve(calculateAverageFps(timestamps));
                            return;
                        }
                        handle = video.requestVideoFrameCallback(callback);
                    };

                    video.onloadedmetadata = () => {
                        if (video.requestVideoFrameCallback) {
                            // Play muted so browser actually decodes frames
                            video.play().catch(() => {});
                            handle = video.requestVideoFrameCallback(callback);
                        } else {
                            clearTimeout(timeout);
                            URL.revokeObjectURL(objectUrl);
                            resolve(null);
                        }
                    };
                });

                if (fpsFromSampling && fpsFromSampling > 10 && fpsFromSampling < 200) {
                    setEstimatedFps(Math.round(fpsFromSampling * 100) / 100);
                } else {
                    setEstimatedFps(null);
                }
            } catch (err) {
                console.error("Framerate detection failed:", err);
                setEstimatedFps(null);
            } finally {
                setIsDetectingFps(false);
            }
        };

        detect();
    }, [videoFile]);

    return {
        estimatedFps,
        isDetectingFps,
        detectionMethod,
    };
}

function calculateAverageFps(timestamps) {
    if (timestamps.length < 5) return null;

    const intervals = [];
    for (let i = 1; i < timestamps.length; i++) {
        const diff = timestamps[i] - timestamps[i - 1];
        if (diff > 0.001) intervals.push(diff);
    }

    if (intervals.length === 0) return null;

    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const fps = 1 / avgInterval;

    if (fps > 20 && fps < 26) return 24;
    if (fps > 24 && fps < 27) return 25;
    if (fps > 28 && fps < 32) return 30;
    if (fps > 58 && fps < 62) return 60;

    return Math.round(fps * 10) / 10;
}
